/**
 * Helpers de zoom y pan del gráfico de sensores.
 *
 * Calculan rangos visibles, formatos del eje X y el tope de
 * `MAX_ZOOM_IN_WINDOW_MS` al hacer zoom.
 *
 * No piden datos a la API: trabajan con los puntos ya cargados.
 */

/**
 * Un minuto en milisegundos.
 *
 * @type {number}
 */
const MINUTE_MS = 60 * 1000

/**
 * Una hora en milisegundos.
 *
 * @type {number}
 */
const HOUR_MS = 60 * MINUTE_MS

/**
 * Un día (24 h) en milisegundos.
 *
 * @type {number}
 */
const DAY_MS = 24 * HOUR_MS

/**
 * Ventana mínima al hacer zoom.
 *
 * No se puede ver menos de `MAX_ZOOM_IN_WINDOW_MS` en el eje X.
 *
 * @type {number}
 */
const MAX_ZOOM_IN_WINDOW_MS = DAY_MS

/**
 * Margen al comparar dos rangos.
 *
 * Evita falsos “hay zoom” por unos milisegundos de redondeo.
 *
 * @type {number}
 */
const RANGE_TOLERANCE_MS = 1500

/**
 * Extrae y ordena las fechas de los puntos del sensor.
 *
 * Cada clave llega en segundos Unix y se convierte a milisegundos para
 * ApexCharts. Si no hay datos, devuelve una lista vacía.
 *
 * @param {Object|string|boolean|null|undefined} sensorChartValues
 *
 * @returns {number[]}
 */
export function getChartTimestampsMs(sensorChartValues) {
    if (!sensorChartValues || typeof sensorChartValues === 'string') {
        return []
    }

    return Object.keys(sensorChartValues)
        .map((timestamp) => parseInt(timestamp, 10) * 1000)
        .filter((timestamp) => !isNaN(timestamp))
        .sort((left, right) => left - right)
}

/**
 * Calcula el intervalo típico entre dos puntos seguidos.
 *
 * Usa la mediana para ignorar huecos grandes (sensor apagado). Si hay menos de
 * 2 puntos, se asume 1 minuto.
 *
 * @param {number[]} timestampsMs
 *
 * @returns {number}
 */
export function getMedianIntervalMs(timestampsMs) {
    if (!timestampsMs || timestampsMs.length < 2) {
        return MINUTE_MS
    }

    const intervals = []

    for (let index = 1; index < timestampsMs.length; index++) {
        const diff = timestampsMs[index] - timestampsMs[index - 1]

        if (diff > 0) {
            intervals.push(diff)
        }
    }

    if (intervals.length === 0) {
        return MINUTE_MS
    }

    intervals.sort((left, right) => left - right)

    return intervals[Math.floor(intervals.length / 2)]
}

/**
 * Devuelve lo más cerca que se puede hacer zoom in, en milisegundos.
 *
 * El tope es `MAX_ZOOM_IN_WINDOW_MS`. Si el filtro o los datos cubren menos
 * tiempo, se usa ese tramo más corto para no pedir un zoom imposible.
 *
 * @param {number[]} timestampsMs
 * @param {number} [boundsSpanMs]
 *
 * @returns {number}
 */
export function getMinZoomWindowMs(timestampsMs, boundsSpanMs) {
    let availableSpan = typeof boundsSpanMs === 'number' && boundsSpanMs > 0
        ? boundsSpanMs
        : MAX_ZOOM_IN_WINDOW_MS

    if (timestampsMs && timestampsMs.length >= 2) {
        const dataSpan = timestampsMs[timestampsMs.length - 1] - timestampsMs[0]

        if (dataSpan > 0) {
            availableSpan = Math.min(availableSpan, dataSpan)
        }
    }

    return Math.min(availableSpan, MAX_ZOOM_IN_WINDOW_MS)
}

/**
 * Elige el nivel del eje X según el rango visible.
 *
 * Cuanto más corto es el rango, más detalle. El orden es: meses → días → horas
 * → minutos → segundos.
 *
 * Así las etiquetas no se solapan.
 *
 * @param {number} minMs
 * @param {number} maxMs
 *
 * @returns {'seconds'|'minutes'|'hours'|'days'|'months'}
 */
export function getTimeSpanForRange(minMs, maxMs) {
    if (minMs == null || maxMs == null || maxMs <= minMs) {
        return 'hours'
    }

    const duration = maxMs - minMs

    if (duration <= 15 * MINUTE_MS) {
        return 'seconds'
    }

    if (duration <= 6 * HOUR_MS) {
        return 'minutes'
    }

    if (duration <= 2 * DAY_MS) {
        return 'hours'
    }

    if (duration <= 90 * DAY_MS) {
        return 'days'
    }

    return 'months'
}

/**
 * Formato Moment de las etiquetas del eje X.
 *
 * Encaja con el nivel que devolvió `getTimeSpanForRange()`.
 *
 * - Segundos: `"DD/MM HH:mm:ss"`
 * - Minutos: `"DD/MM HH:mm"`
 * - Horas: `"DD/MM HH:mm"`
 * - Días: `"DD MMM"`
 * - Meses: `"MMM YYYY"`
 *
 * @param {'seconds'|'minutes'|'hours'|'days'|'months'} span
 *
 * @returns {string}
 */
export function getMomentFormatForSpan(span) {
    switch (span) {
        case 'seconds':
            return 'DD/MM HH:mm:ss'

        case 'minutes':
        case 'hours':
            return 'DD/MM HH:mm'

        case 'days':
            return 'DD MMM'

        case 'months':
            return 'MMM YYYY'

        default:
            return 'DD/MM HH:mm'
    }
}

/**
 * Formato Moment del tooltip al pasar el cursor.
 *
 * Es más completo que el del eje. Incluye fecha y hora (y segundos si el
 * rango es muy corto) para ver el instante exacto del punto.
 *
 * - Segundos: `"DD/MM/YYYY - HH:mm:ss"`
 * - Minutos, horas, días y meses: `"DD/MM/YYYY - HH:mm"`
 *
 * @param {'seconds'|'minutes'|'hours'|'days'|'months'} span
 *
 * @returns {string}
 */
export function getTooltipMomentFormatForSpan(span) {
    switch (span) {
        case 'seconds':
            return 'DD/MM/YYYY - HH:mm:ss'

        default:
            return 'DD/MM/YYYY - HH:mm'
    }
}

/**
 * Mapas de formato nativo de ApexCharts por unidad de tiempo.
 *
 * Se usan si el gráfico pinta ticks solo, sin nuestro formateador de Moment.
 *
 * @returns {Object}
 */
export function getDatetimeFormatter() {
    return {
        year: 'yyyy',
        month: "MMM 'yy",
        day: 'dd MMM',
        hour: 'dd/MM HH:mm',
        minute: 'dd/MM HH:mm',
        second: 'dd/MM HH:mm:ss'
    }
}

/**
 * Ajusta un rango pedido para que sea válido y no pase el tope.
 *
 * Recorta al filtro cargado. No deja ver menos de `MAX_ZOOM_IN_WINDOW_MS` (o el
 * tramo entero si es más corto) y centra el recorte si hace falta.
 *
 * Si el rango no es numérico, vuelve al rango completo.
 *
 * @param {number} requestedMin
 * @param {number} requestedMax
 * @param {number} boundsMin
 * @param {number} boundsMax
 * @param {number} minWindowMs
 *
 * @returns {{ min: number, max: number }}
 */
export function constrainZoomRange(
    requestedMin,
    requestedMax,
    boundsMin,
    boundsMax,
    minWindowMs
) {
    let min = Number(requestedMin)
    let max = Number(requestedMax)

    if (
        isNaN(min)
        || isNaN(max)
        || max <= min
        || isNaN(boundsMin)
        || isNaN(boundsMax)
        || boundsMax <= boundsMin
    ) {
        return {
            min: boundsMin,
            max: boundsMax
        }
    }

    const boundsSpan = boundsMax - boundsMin
    const windowMs = Math.min(
        Math.max(minWindowMs, MAX_ZOOM_IN_WINDOW_MS),
        boundsSpan
    )

    if (min < boundsMin) {
        const shift = boundsMin - min

        min += shift
        max += shift
    }

    if (max > boundsMax) {
        const shift = max - boundsMax

        min -= shift
        max -= shift
    }

    min = Math.max(boundsMin, min)
    max = Math.min(boundsMax, max)

    if (max - min < windowMs) {
        const center = (min + max) / 2

        min = center - windowMs / 2
        max = center + windowMs / 2

        if (min < boundsMin) {
            min = boundsMin
            max = Math.min(boundsMax, min + windowMs)
        }
        if (max > boundsMax) {
            max = boundsMax
            min = Math.max(boundsMin, max - windowMs)
        }
    }

    return {
        min,
        max
    }
}

/**
 * Calcula un zoom in/out alrededor de un punto del eje X.
 *
 * `factor` menor que 1 acerca; mayor que 1 aleja. `anchorRatio` es dónde está
 * el cursor (0 izquierda, 1 derecha). Luego se aplica el tope con
 * `constrainZoomRange()`.
 *
 * @param {number} currentMin
 * @param {number} currentMax
 * @param {number} factor
 * @param {number} anchorRatio
 * @param {number} boundsMin
 * @param {number} boundsMax
 * @param {number} minWindowMs
 *
 * @returns {{ min: number, max: number }}
 */
export function zoomRangeAround(
    currentMin,
    currentMax,
    factor,
    anchorRatio,
    boundsMin,
    boundsMax,
    minWindowMs
) {
    const span = currentMax - currentMin

    if (span <= 0) {
        return {
            min: boundsMin,
            max: boundsMax
        }
    }

    const ratio = typeof anchorRatio === 'number' && !isNaN(anchorRatio)
        ? Math.min(1, Math.max(0, anchorRatio))
        : 0.5

    const newSpan = span * factor
    const anchor = currentMin + span * ratio
    const nextMin = anchor - newSpan * ratio
    const nextMax = anchor + newSpan * (1 - ratio)

    return constrainZoomRange(
        nextMin,
        nextMax,
        boundsMin,
        boundsMax,
        minWindowMs
    )
}

/**
 * Distancia entre los dos dedos de un pinch.
 *
 * @param {TouchList|Array<{ clientX: number, clientY: number }>} touches
 * @returns {number}
 */
export function getPinchDistance(touches) {
    if (!touches || touches.length < 2) {
        return 0
    }

    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY

    return Math.hypot(dx, dy)
}

/**
 * Punto medio horizontal de uno o dos toques.
 *
 * @param {TouchList|Array<{ clientX: number }>} touches
 * @returns {number}
 */
export function getTouchesMidX(touches) {
    if (!touches || touches.length === 0) {
        return 0
    }

    if (touches.length === 1) {
        return touches[0].clientX
    }

    return (touches[0].clientX + touches[1].clientX) / 2
}

/**
 * Convierte una X de pantalla en ratio 0–1 sobre el ancho del gráfico.
 *
 * @param {number} clientX
 * @param {DOMRect|{ left: number, width: number }|null} rect
 * @returns {number}
 */
export function getAnchorRatioFromClientX(clientX, rect) {
    if (
        !rect
        || rect.width <= 0
        || typeof clientX !== 'number'
        || isNaN(clientX)
    ) {
        return 0.5
    }

    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
}

/**
 * Dice si dos rangos son el mismo a efectos de UI.
 *
 * Sirve para saber si hay zoom activo o si ya se volvió al rango del filtro.
 * Ignora diferencias menores que `RANGE_TOLERANCE_MS`.
 *
 * @param {{ min: number, max: number }|null} left
 * @param {{ min: number, max: number }|null} right
 * @param {number} [toleranceMs]
 *
 * @returns {boolean}
 */
export function isSameRange(left, right, toleranceMs = RANGE_TOLERANCE_MS) {
    if (!left || !right) {
        return false
    }

    return Math.abs(left.min - right.min) <= toleranceMs
        && Math.abs(left.max - right.max) <= toleranceMs
}

/**
 * Pasa el filtro de fechas de la pantalla a un rango en milisegundos.
 *
 * Entiende presets (hoy, ayer, semana, mes, trimestre) y el rango custom. El
 * final no pasa de “ahora”. Si el filtro no vale, devuelve `null`.
 *
 * @param {Object|null|undefined} dateFilter
 *
 * @returns {{ min: number, max: number }|null}
 */
export function resolveFilterRangeMs(dateFilter) {
    if (!dateFilter) {
        return null
    }

    const dateType = dateFilter.type_date || dateFilter.dateRange

    if (!dateType) {
        return null
    }

    const now = Date.now()

    let start = null
    let end = null

    switch (dateType) {
        case 'today':
            start = moment().subtract(24, 'hours')
            end = moment()

            break

        case 'yesterday':
            start = moment().subtract(1, 'day').startOf('day')
            end = moment().subtract(1, 'day').endOf('day')

            break

        case 'week':
            start = moment().subtract(1, 'week').startOf('day')
            end = moment()

            break

        case 'month':
            start = moment().subtract(1, 'month').startOf('day')
            end = moment()

            break

        case 'quarter':
            start = moment().subtract(3, 'months').startOf('day')
            end = moment()

            break

        case 'custom': {
            const startDate = (dateFilter.date && dateFilter.date.start_date)
                || dateFilter.customStartDate

            const endDate = (dateFilter.date && dateFilter.date.end_date)
                || dateFilter.customEndDate

            if (!startDate || !endDate) {
                return null
            }

            start = moment(startDate).startOf('day')
            end = moment(endDate).endOf('day')

            break
        }

        default:
            return null
    }

    if (!start || !end || !start.isValid() || !end.isValid()) {
        return null
    }

    return {
        min: start.valueOf(),
        max: Math.min(end.valueOf(), now)
    }
}
